/**
 * Pricing — v2: modular per-persona pricing.
 * ──────────────────────────────────────────
 * The user picks an entity type (Investor / Startup / Holding / Incubator)
 * and then ticks which modules they want. Total updates live. The submit
 * CTA routes to /signup with the chosen modules so onboarding can carry
 * the selection forward.
 *
 * Annual saving ribbon shows the equivalent in annual prepay terms
 * (12-month × 0.85 = 15% discount baked into Annual subscription).
 *
 * Brand: respects the day/night theme via CSS vars + .dark classes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Users, Rocket, Building2, GraduationCap, Check, Lock, ArrowRight,
  Sparkles, Wallet, ShieldAlert, FileSignature, Briefcase, TrendingUp,
  ShieldCheck, Network, Compass,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PublicNavbar from '@/components/PublicNavbar';
import NetworkCanvas from '@/components/NetworkCanvas';
import AuditIntegrityPill from '@/components/AuditIntegrityPill';
import SovereigntyBanner from '@/components/SovereigntyBanner';
import { BrochureLangMenu } from '@/components/BrochureLangMenu';
import { OOHBriefSection } from '@/components/OOH';
import { useTheme } from '@/context/ThemeContext';
import DemoRequestModal from './pricing/DemoRequestModal';

const PERSONA_ICONS = {
  investor:  Users,
  startup:   Rocket,
  holding:   Building2,
  incubator: GraduationCap,
};

const MODULE_ICONS = {
  'shield-check':   ShieldCheck,
  'network':        Network,
  'wallet':         Wallet,
  'building':       Building2,
  'briefcase':      Briefcase,
  'shield-alert':   ShieldAlert,
  'sparkles':       Sparkles,
  'rocket':         Rocket,
  'file-signature': FileSignature,
  'trending-up':    TrendingUp,
  'graduation-cap': GraduationCap,
  'users':          Users,
  'compass':        Compass,
};

function fmtQar(amount) {
  return `${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })} QAR`;
}

export default function Pricing() {
  const { effective } = useTheme();
  const { user } = useAuth();
  const isDark = effective === 'dark';
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === '1';
  const urlPersona = searchParams.get('persona');
  const [catalog, setCatalog] = useState(null);
  const [persona, setPersona] = useState(
    ['startup', 'investor', 'holding', 'incubator'].includes(urlPersona) ? urlPersona : 'startup'
  );
  const [selected, setSelected] = useState(new Set());
  const [billing, setBilling] = useState('monthly');  // 'monthly' | 'annual'
  const [demoOpen, setDemoOpen] = useState(false);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on initial-fetch effects */
  useEffect(() => {
    api.get('/billing/modules')
      .then(({ data }) => {
        setCatalog(data);
        // Pre-select the suggested bundle for the default persona
        const bundle = data.suggested_bundles?.[persona] || [];
        setSelected(new Set(bundle));
      })
      .catch(() => toast.error('Could not load pricing.'));
  }, []);

  // When persona changes, swap to that persona's suggested bundle and
  // drop any modules that aren't available for the new persona.
  useEffect(() => {
    if (!catalog) return;
    const bundle = catalog.suggested_bundles?.[persona] || [];
    setSelected(new Set(bundle));
  }, [persona]);
  /* eslint-enable */

  const personaMeta = useMemo(
    () => catalog?.personas?.find((p) => p.id === persona),
    [catalog, persona]
  );
  const accent = personaMeta?.accent || '#8B3FC5';

  const toggle = (mod) => {
    if (mod.required) return;
    if (!mod.available_for.includes(persona)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mod.id)) next.delete(mod.id);
      else next.add(mod.id);
      return next;
    });
  };

  // Total calc
  const monthlySum = useMemo(() => {
    if (!catalog) return 0;
    return catalog.modules
      .filter((m) => selected.has(m.id))
      .reduce((acc, m) => acc + (m.price_qar_month || 0), 0);
  }, [catalog, selected]);
  const annualSum = Math.round(monthlySum * 12 * 0.85);  // 15% annual discount
  const total = billing === 'annual' ? annualSum : monthlySum;
  const totalLabel = billing === 'annual' ? `${fmtQar(annualSum)} / year` : `${fmtQar(monthlySum)} / month`;
  const savings = billing === 'annual' ? Math.round(monthlySum * 12 - annualSum) : 0;

  const startCheckout = () => {
    // Carry the selection forward — either into the signup form (visitors
    // see prices first, then commit) OR into the dashboard subscribe flow
    // (already-logged-in users).
    const params = new URLSearchParams({
      persona,
      billing,
      modules: Array.from(selected).join(','),
    });
    if (user) {
      // TODO: a real subscribe endpoint takes the carried plan and creates
      // the subscription. For now, take logged-in users to their dashboard
      // with the plan preserved as a query string — operators can wire the
      // Stripe checkout session here later.
      window.location.href = `/dashboard?subscribed=1&${params.toString()}`;
    } else {
      window.location.href = `/signup?${params.toString()}`;
    }
  };

  if (!catalog) {
    return (
      <div className="min-h-screen bg-[var(--ve-canvas)] dark:bg-[#0A0A0A] flex items-center justify-center text-slate-500">
        Loading pricing…
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen text-[var(--ve-black)] dark:text-white"
      data-testid="pricing-page"
      style={{
        // v266.6 — unified with Login/Home lavender palette. Boosted
        // violet stops from 0.10/0.08/0.05 → 0.16/0.14/0.10 so the
        // ambient wash actually reads on a lavender base. Base color
        // pinned to `var(--ve-bg)` so future palette shifts propagate
        // automatically.
        background: isDark
          ? 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.18), transparent 60%),'
            + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.16), transparent 60%),'
            + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.08), transparent 70%),'
            + ' #000000'
          : 'radial-gradient(1300px 700px at 85% -10%, rgba(174,82,234,0.16), transparent 60%),'
            + ' radial-gradient(1000px 700px at -10% 110%, rgba(94,31,139,0.14), transparent 60%),'
            + ' radial-gradient(900px 900px at 50% 50%, rgba(121,50,174,0.10), transparent 70%),'
            + ' var(--ve-bg)',
      }}
    >
      {/* v163 — Shared <NetworkCanvas /> behind the page so the visual
          identity matches the home page exactly. */}
      <NetworkCanvas isDark={isDark} testId="pricing-network-canvas" />

      <div className="relative z-10">
      {/* Unified public navigation */}
      <PublicNavbar />

      {/* Hero */}
      <section className="ve-container pt-6 pb-8 md:pt-12 md:pb-12 text-center">
        {isWelcome && (
          <div
            className="inline-flex items-center gap-2 px-4 py-2 mb-5 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold border bg-emerald-50 border-emerald-300 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-400/40 dark:text-emerald-300"
            data-testid="pricing-welcome-banner"
          >
            <Check size={12} /> Account created · pick your modules to start
          </div>
        )}
        {!isWelcome && !user && (
          <div
            className="inline-flex items-center gap-2 px-4 py-2 mb-5 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold border bg-[rgba(174,82,234,0.10)] border-[rgba(174,82,234,0.35)] text-[var(--ve-violet-shadow)] dark:text-[var(--ve-violet-bright)] dark:bg-[rgba(174,82,234,0.10)] dark:border-[rgba(174,82,234,0.40)]"
            data-testid="pricing-preview-banner"
          >
            <Sparkles size={12} /> Step 1 of 2 · Pick your modules · then create your account
          </div>
        )}
        <div className="ve-eyebrow inline-flex text-[var(--ve-violet-bright)]">Modular · Sovereign · Transparent</div>
        <h1 className="display mt-4 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--ve-black)] dark:text-white">
          Build your<br className="md:hidden" /> <span className="ve-violet-ink">Vigilant.</span>
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-lg md:text-xl text-slate-700 dark:text-slate-300 leading-relaxed">
          Pick your entity type, choose only the modules you need. Pricing scales with you — never more, never opaque.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <SovereigntyBanner size="sm" />
          <BrochureLangMenu
            testId="pricing-download-brochure"
            label="Sales Brochure (PDF)"
            iconSize={12}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold border border-[var(--ve-violet-bright)]/40 text-[var(--ve-violet-shadow)] dark:text-[var(--ve-violet-bright)] bg-white/70 dark:bg-white/[0.04] hover:bg-white dark:hover:bg-white/[0.08] transition-colors"
          />
        </div>
      </section>

      {/* Persona selector */}
      <section className="ve-container pb-10" data-testid="pricing-persona-selector">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 dark:text-slate-400 mb-3">
          Step 1 · Choose your entity type
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {catalog.personas.map((p) => {
            const Icon = PERSONA_ICONS[p.id] || Users;
            const isActive = persona === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPersona(p.id)}
                data-testid={`pricing-persona-${p.id}`}
                className={`text-left p-5 rounded-xl border-2 transition-all ${
                  isActive
                    ? 'bg-white dark:bg-[#141414] shadow-lg'
                    : 'bg-white/60 dark:bg-[#0F0F0F]/70 hover:bg-white/90'
                }`}
                style={{
                  borderColor: isActive ? p.accent : 'rgba(192,192,192,0.4)',
                }}
              >
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${p.accent}1a`, color: p.accent }}>
                  <Icon size={18} strokeWidth={1.8} />
                </div>
                <div className="mt-3 ve-eyebrow" style={{ color: p.accent }}>{p.label}</div>
                <div className="mt-2 text-[12px] text-slate-600 dark:text-slate-300">
                  {p.id === 'investor'  && 'Funds, family offices, angels'}
                  {p.id === 'startup'   && 'Founders, scaleups, ventures'}
                  {p.id === 'holding'   && 'Sovereign holdings, groups'}
                  {p.id === 'incubator' && 'Incubators, accelerators, programs'}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Modules + sticky summary */}
      <section className="ve-container pb-16 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 dark:text-slate-400 mb-3">
            Step 2 · Pick your modules
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-modules-grid">
            {catalog.modules.map((m) => {
              const Icon = MODULE_ICONS[m.icon] || Sparkles;
              const isAvailable = m.available_for.includes(persona);
              const isSelected = selected.has(m.id);
              const isRequired = m.required;
              const isHeadline = m.headline;
              return (
                <button
                  key={m.id}
                  onClick={() => toggle(m)}
                  disabled={!isAvailable || isRequired}
                  data-testid={`pricing-module-${m.id}`}
                  className={`text-left p-5 rounded-xl border-2 transition-all relative overflow-hidden ${
                    isSelected
                      ? 'bg-white dark:bg-[#141414] shadow-md'
                      : isAvailable
                        ? 'bg-white/60 dark:bg-[#0F0F0F]/70 hover:bg-white/90 dark:hover:bg-[#141414]/90'
                        : 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-[#0A0A0A]'
                  }`}
                  style={{
                    borderColor: isSelected ? accent : 'rgba(192,192,192,0.35)',
                  }}
                >
                  {isHeadline && (
                    <div
                      className="absolute top-0 right-0 text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-bl-md"
                      style={{ backgroundColor: accent, color: '#0A0A0A' }}
                    >
                      Headline
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1a`, color: accent }}>
                      <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {!isAvailable ? (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1">
                          <Lock size={11} /> N/A for {persona}
                        </span>
                      ) : isRequired ? (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <Check size={11} /> Required
                        </span>
                      ) : (
                        <div
                          className={`h-5 w-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'border-transparent' : 'border-slate-300 dark:border-slate-600'}`}
                          style={{ backgroundColor: isSelected ? accent : 'transparent' }}
                        >
                          {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-baseline justify-between gap-3">
                    <h3 className="display text-lg md:text-xl leading-tight">{m.name}</h3>
                    <div className="text-right shrink-0">
                      {m.free ? (
                        <div className="display text-base tabular-nums px-2 py-0.5 rounded-md border" style={{
                          color: '#10B981',
                          borderColor: 'rgba(16,185,129,0.40)',
                          backgroundColor: 'rgba(16,185,129,0.10)',
                        }}
                        data-testid={`pricing-module-${m.id}-free-badge`}>
                          FREE
                        </div>
                      ) : (
                        <>
                          <div className="display text-base tabular-nums" style={{ color: accent }}>
                            {fmtQar(m.price_qar_month)}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">/ month</div>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">
                    {m.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sticky summary */}
        <aside className="lg:col-span-4">
          <div
            className="sticky top-6 rounded-2xl border-2 bg-white dark:bg-[#141414] p-6 md:p-7"
            style={{ borderColor: `${accent}50` }}
            data-testid="pricing-summary"
          >
            <div className="ve-eyebrow" style={{ color: accent }}>
              {personaMeta?.label} build
            </div>
            <div className="display text-3xl md:text-4xl mt-2 leading-tight">
              {totalLabel}
            </div>
            {billing === 'annual' && savings > 0 && (
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-semibold">
                You save {fmtQar(savings)} / year
              </div>
            )}

            {/* Billing toggle */}
            <div className="mt-5 flex items-center gap-1 p-1 rounded-lg bg-slate-100 dark:bg-[#0A0A0A]">
              {['monthly', 'annual'].map((b) => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  data-testid={`pricing-billing-${b}`}
                  className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold rounded-md transition-all ${
                    billing === b
                      ? 'bg-white dark:bg-[#141414] shadow-sm text-[var(--ve-black)] dark:text-white'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {b}
                  {b === 'annual' && <span className="ml-1 text-emerald-600 dark:text-emerald-400">−15%</span>}
                </button>
              ))}
            </div>

            {/* Selected modules list */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">
                Modules · {selected.size}
              </div>
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-2">
                {Array.from(selected).map((id) => {
                  const m = catalog.modules.find((x) => x.id === id);
                  if (!m) return null;
                  return (
                    <div key={id} className="flex items-center justify-between text-[12px]">
                      <span className="text-slate-700 dark:text-slate-200 truncate">{m.name}</span>
                      <span className={`tabular-nums shrink-0 ml-2 ${m.free ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                        {m.free ? 'FREE' : fmtQar(m.price_qar_month)}
                      </span>
                    </div>
                  );
                })}
                {selected.size === 0 && (
                  <div className="text-[12px] text-slate-500 italic">No modules selected.</div>
                )}
              </div>
            </div>

            <button
              onClick={startCheckout}
              disabled={selected.size === 0}
              data-testid="pricing-checkout-cta"
              className={`mt-6 w-full justify-center ${monthlySum === 0 && selected.size > 0 ? 've-btn-emerald' : 've-btn-primary'}`}
              style={monthlySum === 0 && selected.size > 0 ? {
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                border: 'none',
                padding: '14px 24px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.22em',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              } : undefined}
            >
              {monthlySum === 0 && selected.size > 0
                ? (<><Check size={14} /> Activate complimentary plan</>)
                : user
                  ? (<>Start with this plan <ArrowRight size={14} /></>)
                  : (<>Continue to sign up <ArrowRight size={14} /></>)}
            </button>
            {monthlySum === 0 && selected.size > 0 && (
              <p
                className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 text-center font-semibold"
                data-testid="pricing-complimentary-note"
              >
                Every selected module is on the house — no card required.
              </p>
            )}
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              data-testid="pricing-demo-cta"
              className="mt-3 w-full px-4 py-3 text-[11px] uppercase tracking-[0.22em] font-bold rounded border border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-shadow)] dark:text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.06)] transition-colors"
            >
              Or — contact us for a demo
            </button>
            <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 text-center">
              {user
                ? 'Cancel anytime.'
                : 'Step 2 of 2 · Create your account · No commitments before you decide.'}
            </p>
            <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Included with every plan</div>
              <div className="space-y-1.5 text-[12px] text-slate-600 dark:text-slate-300">
                <Inc>White-label subdomain</Inc>
                <Inc>10 admin seats (more on request)</Inc>
                <Inc>Audit-grade activity log</Inc>
                <Inc>Email + WhatsApp expiry alerts</Inc>
              </div>
            </div>
          </div>
        </aside>
      </section>

      {/* OMNISCIENT ORBITAL HARMONY — patent-pending IP brief */}
      <section className="ve-container pb-12" data-testid="pricing-ooh">
        <OOHBriefSection compact />
      </section>

      {/* FAQ-lite reassurance strip */}
      <section className="ve-container pb-16 grid md:grid-cols-3 gap-4">
        <FaqPill q="Can I switch modules later?" a="Yes. Mix and match anytime from your billing dashboard — pro-rated mid-month." />
        <FaqPill q="What about white-labelling?" a="Holdings and Incubators get a branded subdomain on every plan." />
        <FaqPill q="Sovereign data residency?" a="Available on Annual. Choose your region; data never leaves it." />
      </section>

      <footer className="border-t border-slate-200/50 dark:border-white/10">
        <div className="ve-container py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
              © 2026 Vigilant Entities
            </div>
            <AuditIntegrityPill />
          </div>
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.18em] font-bold">
            <Link to="/" className="hover:text-[var(--ve-violet-bright)] transition-colors">Home</Link>
            <Link to="/login" className="hover:text-[var(--ve-violet-bright)] transition-colors">Sign In</Link>
            <Link to="/signup" className="hover:text-[var(--ve-violet-bright)] transition-colors">Create Account</Link>
          </div>
        </div>
      </footer>

      {demoOpen && (
        <DemoRequestModal
          onClose={() => setDemoOpen(false)}
          persona={persona}
          modules={Array.from(selected)}
        />
      )}
      </div>
    </div>
  );
}

function Inc({ children }) {
  return (
    <div className="flex items-start gap-2">
      <Check size={13} className="text-emerald-500 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function FaqPill({ q, a }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#141414]/80 p-5">
      <div className="text-sm font-bold">{q}</div>
      <div className="text-[13px] text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">{a}</div>
    </div>
  );
}
